import React, { useState, useEffect, useLayoutEffect, useRef, useContext } from "react";
import { createPortal } from "react-dom";
import { useAsyncFn } from "../../hooks/use-async-fn";
import DownIcon from "../../assets/DownIcon";
import "./AttributePopup.css";
import InfoPopup from "../InfoPopup";
import { AuthContext } from "../../contexts/auth-context";
import { Tooltip } from "../Tooltip";
type AttrValue = string | boolean | number;
const ATTRIBUTE_INPUT_MAX_LENGTH = 30;
const isRangeableNumericType = (rawType: string) =>
  rawType === "int" ||
  rawType === "integer" ||
  rawType.startsWith("float") ||
  rawType.startsWith("double") ||
  rawType.startsWith("decimal");
const isRangeAttrType = (attrType: unknown) =>
  typeof attrType === "string" && attrType.endsWith("_range");

const DATA_TYPE_TOOLTIPS: Record<string, string> = {
  int: "Whole numbers only - no decimals (e.g., 25, 100)",
  integer: "Whole numbers only - no decimals (e.g., 25, 100)",
  float: "Numbers that can include decimals (e.g., 12.5, 99.75)",
  double: "Numbers with many decimal places (e.g., 12345.678901)",
  decimal: "Exact decimal amounts (e.g., 12.50, 99.99)",
  varchar: "Any text - words, codes, or labels (e.g., Gold, Premium User, ABC123)",
  string: "Any text - words, codes, or labels (e.g., Gold, Premium User, ABC123)",
  boolean: "Only True or False",
  date: "A calendar date in YYYY-MM-DD format (e.g., 2026-07-03)",
  timestamp: "A date and time in YYYY-MM-DD HH:MM:SS format (e.g., 2026-07-03 10:30:00)",
};

const getDataTypeTooltip = (rawType: string): string | undefined => {
  if (!rawType) return undefined;
  const normalized = rawType.toLowerCase().replace(/_range$/, "");
  if (DATA_TYPE_TOOLTIPS[normalized]) return DATA_TYPE_TOOLTIPS[normalized];
  const baseKey = Object.keys(DATA_TYPE_TOOLTIPS).find((key) =>
    normalized.startsWith(key),
  );
  return baseKey ? DATA_TYPE_TOOLTIPS[baseKey] : undefined;
};

const TypeBadge = ({ type }: { type: string }) => {
  const tip = getDataTypeTooltip(type);
  const badge = (
    <span className={`typeBadge${tip ? " typeBadge-hint" : ""}`}>{type}</span>
  );
  if (!tip) return badge;
  return (
    <Tooltip text={tip} placement="top" maxWidth={280}>
      {badge}
    </Tooltip>
  );
};
interface AttributePopupProps {
  onClose: () => void;
  onSave: (attributeValues: Record<string, string[]>) => void;
  setAttributesChips?: React.Dispatch<
    React.SetStateAction<Record<string, AttrValue[]>>
  >;
  setAttributeType?: React.Dispatch<
    React.SetStateAction<Record<string, AttrValue>>
  >;
  addedAttributes?: Record<string, AttrValue[]>;
  addedAttributeTypes?: Record<string, AttrValue>;
  initialChips?: Record<string, AttrValue[]>;
  initialAttributeTypes?: Record<string, AttrValue>;
  popupRef: any;
  databaseTable: any;
  SourceId: any;
  matchId?: string;
}

const AttributePopup = ({
  onClose,
  onSave,
  setAttributesChips,
  addedAttributes,
  addedAttributeTypes,
  initialChips,
  initialAttributeTypes,
  popupRef,
  databaseTable,
  SourceId,
  setAttributeType,
  matchId,
}: AttributePopupProps) => {
  const { getSchema, appMetadata, getTablesByName } = useContext(AuthContext);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dropdownOptionsRef = useRef<HTMLDivElement | null>(null);
  const [attributeValues, setAttributeValues] = useState<
    Record<string, string[]>
  >({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  type AttrValue = string | boolean | number;

  const [intInputMode, setIntInputMode] = useState<Record<string, "values" | "range">>(() => {
    if (!initialAttributeTypes) return {};
    return Object.fromEntries(
      Object.entries(initialAttributeTypes)
        .filter(([, t]) => isRangeAttrType(t))
        .map(([k]) => [k, "range" as const])
    );
  });

  const [rangeValues, setRangeValues] = useState<Record<string, { min: string; max: string }>>(() => {
    if (!initialChips || !initialAttributeTypes) return {};
    return Object.fromEntries(
      Object.entries(initialAttributeTypes)
        .filter(([, t]) => isRangeAttrType(t))
        .map(([k]) => [k, { min: String(initialChips[k]?.[0] ?? ""), max: String(initialChips[k]?.[1] ?? "") }])
    );
  });

  const [currentInput, setCurrentInput] = useState<Record<string, string>>({});
  const [showAttrAddedSuccess, setShowAttrAddedSuccess] = useState(false);

  const [selectedAttributes, setSelectedAttributes] = useState<string[]>(
    () => initialChips ? Object.keys(initialChips) : []
  );

  const [attributeChips, setAttributeChips] = useState<Record<string, AttrValue[]>>(() => {
    if (!initialChips) return {};
    return Object.fromEntries(
      Object.entries(initialChips).map(([k, vals]) =>
        isRangeAttrType(initialAttributeTypes?.[k]) ? [k, []] : [k, vals]
      )
    );
  });

  const [attributeInputValues, setAttributeInputValues] = useState<
    Record<string, string>
  >({});

  const federationId = localStorage.getItem("federationkey");
  var userOrganization;

  let tenantObj = {};
  tenantObj = appMetadata && Object.values(appMetadata);
  userOrganization = tenantObj[0].organization;
  const getSchemaFn = useAsyncFn({
    deferFn: getSchema,
    federationId: federationId,
    organizationId: userOrganization,
  });

  const parseValue = (val: string, attrType?: string): AttrValue => {
    const trimmed = val.trim();
    if (isRangeableNumericType(attrType ?? "") && trimmed !== "" && !isNaN(Number(trimmed)))
      return Number(trimmed);
    return trimmed;
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    attr: string,
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();

      const raw = attributeInputValues[attr];
      if (!raw || raw.trim() === "") return;

      const colType = attributesResult
        ?.find((a) => a.Name === attr)
        ?.Type?.toLowerCase();
      const value = parseValue(raw, colType);

      setAttributeChips((prev) => ({
        ...prev,
        [attr]: [...(prev[attr] || []), value],
      }));

      setAttributeInputValues((prev) => ({
        ...prev,
        [attr]: "",
      }));
    }
  };

  const handleAddDefaultBoolean = (attr: string) => {
    setAttributeChips((prev) => ({
      ...prev,
      [attr]: ["True", "False"],
    }));
  };

  const getTablesNameFn = useAsyncFn({
    deferFn: getTablesByName,
  });

  const attributesResult = getTablesNameFn?.data && getTablesNameFn?.data?.Cols;

  const handleChipRemove = (chip: AttrValue, attr: string) => {
    setAttributeChips((prev) => ({
      ...prev,
      [attr]: (prev[attr] || []).filter((c) => c !== chip),
    }));
  };


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    attr: string,
  ) => {
    const nextValue = e.target.value.slice(0, ATTRIBUTE_INPUT_MAX_LENGTH);
    setAttributeInputValues((prev) => ({
      ...prev,
      [attr]: nextValue,
    }));
  };

  const calcDropdownPos = (rect: DOMRect) => {
    const dropdownWidth = Math.min(580, window.innerWidth - 16);
    const left = Math.min(rect.left, Math.max(8, window.innerWidth - dropdownWidth - 8));
    return { top: rect.bottom + 4, left, width: rect.width };
  };

  const handleToggleDropdown = () => {
    if (!isDropdownOpen && dropdownRef.current) {
      setDropdownPos(calcDropdownPos(dropdownRef.current.getBoundingClientRect()));
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  useLayoutEffect(() => {
    if (!isDropdownOpen || !dropdownRef.current) return;
    const next = calcDropdownPos(dropdownRef.current.getBoundingClientRect());
    setDropdownPos((prev) => {
      if (prev?.top === next.top && prev?.left === next.left && prev?.width === next.width) return prev;
      return next;
    });
  }, [isDropdownOpen, selectedAttributes.length]);

  const handleAttributeSelect = (attr) => {
    if (selectedAttributes.includes(attr.Name)) {
      removeAttribute(attr.Name);
    } else {
      setSelectedAttributes([...selectedAttributes, attr.Name]);
      setAttributeValues({ ...attributeValues, [attr.Name]: [] });
      setCurrentInput({ ...currentInput, [attr.Name]: "" });
      if (attr.Type === "boolean") {
        handleAddDefaultBoolean(attr.Name);
      }
    }
  };

  const removeAttribute = (attr: string) => {
    setSelectedAttributes((prev) => prev.filter((a) => a !== attr));

    setAttributeValues((prev) => {
      const { [attr]: _removed, ...rest } = prev;
      return rest;
    });

    setCurrentInput((prev) => {
      const { [attr]: _removed, ...rest } = prev;
      return rest;
    });

    setAttributeChips((prev) => {
      const { [attr]: _removed, ...rest } = prev;
      return rest;
    });

    setAttributeInputValues((prev) => {
      const { [attr]: _removed, ...rest } = prev;
      return rest;
    });

    setIntInputMode((prev) => {
      const { [attr]: _removed, ...rest } = prev;
      return rest;
    });

    setRangeValues((prev) => {
      const { [attr]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleSave = () => {
    const attrType = {};
    const chipsToSave = { ...attributeChips };

    selectedAttributes.forEach((attr) => {
      if (intInputMode[attr] === "range") {
        const rv = rangeValues[attr] ?? { min: "", max: "" };
        chipsToSave[attr] = [rv.min, rv.max];
      }
    });

    Object.keys(chipsToSave).forEach((key) => {
      const attribute = attributesResult?.find(
        (attrOption) => attrOption.Name === key,
      );
      const dbType = attribute?.Type?.toLowerCase() ?? "";
      if (dbType === "boolean") {
        attrType[key] = "boolean";
      } else if (dbType === "int" || dbType === "integer") {
        attrType[key] = intInputMode[key] === "range" ? "int_range" : "int";
      } else if (dbType.startsWith("float")) {
        attrType[key] = intInputMode[key] === "range" ? "float_range" : "float";
      } else if (dbType.startsWith("double")) {
        attrType[key] = intInputMode[key] === "range" ? "double_range" : "double";
      } else if (dbType.startsWith("decimal")) {
        attrType[key] = intInputMode[key] === "range" ? "decimal_range" : "decimal";
      } else if (dbType === "timestamp") {
        attrType[key] = "timestamp";
      } else if (dbType.startsWith("varchar")) {
        attrType[key] = "string";
      } else {
        attrType[key] = dbType || "string";
      }
    });
    setAttributeType(attrType);
    setAttributesChips(chipsToSave);
    setShowAttrAddedSuccess(true);
    onSave(attributeValues);
  };

  useEffect(() => {
    if (federationId) {
      getSchemaFn.run({
        federationId: federationId,
        organizationId: userOrganization,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [federationId]);

  useEffect(() => {
    if (databaseTable) {
      getTablesNameFn.run({
        tableName: databaseTable,
        datasourceId: SourceId,
        organizationId: userOrganization,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databaseTable]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const insideDropdownToggle = dropdownRef.current?.contains(target);
      const insideDropdownOptions = dropdownOptionsRef.current?.contains(target);
      if (!insideDropdownToggle && !insideDropdownOptions) {
        setIsDropdownOpen(false);
      }

      if (popupRef.current && !popupRef.current.contains(target) && !insideDropdownOptions) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddAttributeChips = (attr, chip, index) => {
    setAttributeInputValues((prev) => ({ ...prev, [attr]: String(chip) }));
    setAttributeChips((prev) => ({
      ...prev,
      [attr]: (prev[attr] || []).filter((_, i) => i !== index),
    }));
  };

  return (
    <>
      {showAttrAddedSuccess && (
        <InfoPopup
          isOpen={Boolean(showAttrAddedSuccess)}
          onClose={() => {
            setShowAttrAddedSuccess(false);
          }}
          headerText="Attribute(s) added!"
          freeText
        />
      )}
      <div className="popupOverlay" ref={popupRef} onClick={onClose}>
        <div className="popupBox" onClick={(e) => e.stopPropagation()}>
          <div className="popupContent">
            {/* ── Header ── */}
            <div className="popupHeader">
              <div>
                <h2 className="popupHeaderTitle">
                  {addedAttributes ? "Added Attributes" : "Configure Attributes"}
                </h2>
                {!addedAttributes && (
                  <p className="popupHeaderSubtitle">
                    Select attributes and define their allowed values for query
                    filtering.
                  </p>
                )}
              </div>
              <button className="popupCloseBtn" onClick={onClose}>
                ×
              </button>
            </div>

            {/* ── Body ── */}
            <div className="popupBody">
              {addedAttributes ? (
                <div className="added-table-wrapper">
                  <table className="added-table">
                    <thead>
                      <tr>
                        <th>Attribute</th>
                        <th>Data Type</th>
                        <th>Values</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(addedAttributes).map((key) => (
                        <tr key={key}>
                          <td className="added-attr-name">{key}</td>
                          <td>
                            {addedAttributeTypes?.[key] ? (
                              <TypeBadge type={String(addedAttributeTypes[key])} />
                            ) : "—"}
                          </td>
                          <td>
                            <div className="added-value-pills">
                              {addedAttributes[key].map((val, i) => (
                                <span key={i} className="added-value-pill">
                                  {typeof val === "boolean" ? val.toString() : val}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <>
                  {/* Selector toolbar */}
                  <div className="attributesToolbar">
                    <div
                      className="dropdown"
                      onClick={handleToggleDropdown}
                      ref={dropdownRef}
                    >
                      <div className="dropdown-toggle">
                        {selectedAttributes.length > 0
                          ? `Add more attributes`
                          : `Select attributes`}
                        <DownIcon className="dropdown-icon" />
                      </div>
                      {isDropdownOpen && dropdownPos && createPortal(
                        <div
                          ref={dropdownOptionsRef}
                          className="dropdown-options"
                          style={{ top: dropdownPos.top, left: dropdownPos.left }}
                        >
                          {attributesResult?.filter((attr: { Name: string }) => attr.Name !== matchId).map((attr: { Name: string; Type: string }) => (
                            <div
                              key={attr.Name}
                              className={`dropdown-option ${
                                selectedAttributes.includes(attr.Name)
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAttributeSelect(attr);
                              }}
                            >
                              <span title={attr.Name}>{attr.Name}</span>
                              {selectedAttributes.includes(attr.Name) && (
                                <span className="dropdown-option-check">✓</span>
                              )}
                            </div>
                          ))}
                        </div>,
                        document.body
                      )}
                    </div>
                    {selectedAttributes.length > 0 && (
                      <span className="selectedCount">
                        {selectedAttributes.length} attribute
                        {selectedAttributes.length !== 1 ? "s" : ""} selected
                      </span>
                    )}
                  </div>

                  {/* Attributes list or empty state */}
                  {selectedAttributes.length === 0 ? (
                    <div className="attrEmptyState">
                      <div className="attrEmptyIcon">⊕</div>
                      <p className="attrEmptyTitle">No attributes selected</p>
                      <p className="attrEmptySubtitle">
                        Use the dropdown above to pick attributes. For each one,
                        you can specify allowed filter values.
                      </p>
                    </div>
                  ) : (
                    <div className="attributesList">
                      {selectedAttributes.map((attr) => {
                        const attrObj = attributesResult?.find(
                          (a: { Name: string; Type: string }) => a.Name === attr,
                        );
                        const rawType = attrObj?.Type?.toLowerCase() ?? "";
                        const displayType = rawType.startsWith("varchar")
                          ? "varchar"
                          : rawType;
                        const isAttributeBoolean = rawType === "boolean";
                        const isAttributeNumeric = isRangeableNumericType(rawType);
                        const currentIntMode = intInputMode[attr] ?? "values";

                        return (
                          <div key={attr} className="attributeItem">
                            <div className="attributeItemHeader">
                              <span className="attrName">{attr}</span>
                              {displayType && <TypeBadge type={displayType} />}
                              <Tooltip text="Remove attribute" placement="top">
                                <button
                                  className="deleteAttr"
                                  onClick={() => removeAttribute(attr)}
                                >
                                  ×
                                </button>
                              </Tooltip>
                            </div>

                            {isAttributeBoolean ? (
                              <div className="chipInputWrapper">
                                <div className="boolChipContainer">
                                  {["True", "False"].map((chip, index) => (
                                    <div key={index} className="chip">
                                      <div className="chip-text bool-chip">{chip}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : isAttributeNumeric ? (
                              <>
                                <div className="intModeToggle">
                                  <button
                                    type="button"
                                    className={`intModeBtn${currentIntMode === "values" ? " active" : ""}`}
                                    onClick={() => setIntInputMode((prev) => ({ ...prev, [attr]: "values" }))}
                                  >
                                    Specific values
                                  </button>
                                  <button
                                    type="button"
                                    className={`intModeBtn${currentIntMode === "range" ? " active" : ""}`}
                                    onClick={() => setIntInputMode((prev) => ({ ...prev, [attr]: "range" }))}
                                  >
                                    Range
                                  </button>
                                </div>
                                {currentIntMode === "range" ? (
                                  <div className="rangeInputContainer">
                                    <input
                                      type="number"
                                      className="rangeInput"
                                      placeholder="Min"
                                      value={rangeValues[attr]?.min ?? ""}
                                      onChange={(e) =>
                                        setRangeValues((prev) => ({
                                          ...prev,
                                          [attr]: { ...prev[attr], min: e.target.value },
                                        }))
                                      }
                                    />
                                    <span className="rangeSeparator">–</span>
                                    <input
                                      type="number"
                                      className="rangeInput"
                                      placeholder="Max"
                                      value={rangeValues[attr]?.max ?? ""}
                                      onChange={(e) =>
                                        setRangeValues((prev) => ({
                                          ...prev,
                                          [attr]: { ...prev[attr], max: e.target.value },
                                        }))
                                      }
                                    />
                                  </div>
                                ) : (
                                  <div className="chipInputWrapper">
                                    <div className="chipContainer">
                                      {(attributeChips[attr] || []).map((chip, index) => (
                                        <div
                                          key={index}
                                          className="chip"
                                          onClick={() => handleAddAttributeChips(attr, chip, index)}
                                        >
                                          <div className="chip-text">
                                            {typeof chip === "boolean" ? chip.toString() : chip}
                                          </div>
                                          <button
                                            className="chip-close"
                                            onClick={(e) => { e.stopPropagation(); handleChipRemove(chip, attr); }}
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                      <input
                                        type="number"
                                        className="chipInput"
                                        value={attributeInputValues[attr] || ""}
                                        onChange={(e) => handleChange(e, attr)}
                                        onKeyDown={(e) => handleKeyDown(e, attr)}
                                        placeholder={(attributeChips[attr] || []).length === 0 ? "Type a value and press Enter to add…" : "Add another…"}
                                      />
                                    </div>
                                  </div>
                                )}
                                {currentIntMode === "values" && (attributeChips[attr] || []).length === 0 && (
                                  <p className="chipHint">No values added yet — values narrow query filter choices for this attribute.</p>
                                )}
                              </>
                            ) : (
                              <>
                                <div className="chipInputWrapper">
                                  <div className="chipContainer">
                                    {(attributeChips[attr] || []).map(
                                      (chip, index) => (
                                        <div
                                          key={index}
                                          className="chip"
                                          onClick={() =>
                                            handleAddAttributeChips(
                                              attr,
                                              chip,
                                              index,
                                            )
                                          }
                                        >
                                          <div className="chip-text">
                                            {typeof chip === "boolean"
                                              ? chip.toString()
                                              : chip}
                                          </div>
                                          <button
                                            className="chip-close"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleChipRemove(chip, attr);
                                            }}
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ),
                                    )}
                                    <input
                                      type="text"
                                      className="chipInput"
                                      value={attributeInputValues[attr] || ""}
                                      onChange={(e) => handleChange(e, attr)}
                                      onKeyDown={(e) => handleKeyDown(e, attr)}
                                      maxLength={ATTRIBUTE_INPUT_MAX_LENGTH}
                                      placeholder={
                                        (attributeChips[attr] || []).length === 0
                                          ? "Type a value and press Enter to add…"
                                          : "Add another…"
                                      }
                                    />
                                  </div>
                                </div>
                                {(attributeChips[attr] || []).length === 0 && (
                                  <p className="chipHint">
                                    No values added yet — values narrow query
                                    filter choices for this attribute.
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Footer ── */}
            <div className="footerButtons">
              {addedAttributes ? (
                <button className="addBtn" onClick={onClose}>
                  Close
                </button>
              ) : (
                <>
                  <button className="cancelBtn" onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    className="addBtn"
                    onClick={handleSave}
                    disabled={
                      selectedAttributes.length === 0 ||
                      selectedAttributes.some((attr) => {
                        const attrObj = attributesResult?.find(
                          (a: { Name: string; Type: string }) => a.Name === attr,
                        );
                        const rawType = attrObj?.Type?.toLowerCase() ?? "";
                        if (rawType === "boolean") return false;
                        if (isRangeableNumericType(rawType)) {
                          if ((intInputMode[attr] ?? "values") === "range") {
                            const rv = rangeValues[attr];
                            return !rv?.min || !rv?.max;
                          }
                        }
                        return (attributeChips[attr] || []).length === 0;
                      })
                    }
                  >
                    Save Attributes
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AttributePopup;
